import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { useAuth } from '../../auth/useAuth';
import { InviteRecord, Role } from '../../auth/types';
import { trackReads, trackWrite } from '../../services/firebaseUsageTracker';

export const InviteSection: React.FC = () => {
  const { roles, profile } = useAuth();
  const [invites, setInvites] = useState<InviteRecord[]>([]);
  const [form, setForm] = useState<{ email: string; role: Role; expiresInDays: number }>({
    email: '',
    role: 'user',
    expiresInDays: 7,
  });
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isDev = roles.includes('dev');

  // Role options based on caller's role
  const roleOptions: Role[] = isDev ? ['user', 'admin'] : ['user'];

  useEffect(() => {
    if (!profile?.uid) return;

    // Devs see all invites, admins see only their own
    const baseQuery = collection(db, 'invites');
    const q = isDev
      ? query(baseQuery, orderBy('createdAt', 'desc'))
      : query(baseQuery, where('createdBy', '==', profile.uid), orderBy('createdAt', 'desc'));

    const unsub = onSnapshot(q, snap => {
      trackReads('invites', snap.docs.length, 'admin-invites-listener');
      const mapped = snap.docs.map(docSnap => {
        const data = docSnap.data() as Omit<InviteRecord, 'id'>;
        return { id: docSnap.id, ...data };
      });
      setInvites(mapped);
    });

    return () => unsub();
  }, [profile?.uid, isDev]);

  const sendInvite = httpsCallable(functions, 'sendInvite');

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setSending(true);

    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        role: form.role,
        expiresInDays: form.expiresInDays,
      };
      await sendInvite(payload);
      setMessage({ type: 'success', text: 'Invite created and email sent!' });
      setForm({ email: '', role: 'user', expiresInDays: 7 });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to send invite.',
      });
    } finally {
      setSending(false);
    }
  };

  const revokeInvite = async (inviteId: string) => {
    try {
      const ref = doc(db, 'invites', inviteId);
      await updateDoc(ref, { expiresAt: Timestamp.fromDate(new Date()) });
      trackWrite('invites', 'revoke');
      setMessage({ type: 'success', text: 'Invite revoked.' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to revoke invite.',
      });
    }
  };

  const resendInvite = async (invite: InviteRecord) => {
    setMessage(null);
    setSending(true);

    try {
      // Create a new invite with the same email/role
      await sendInvite({
        email: invite.email,
        role: invite.role,
        expiresInDays: 7,
      });
      setMessage({ type: 'success', text: `New invite sent to ${invite.email}` });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to resend invite.',
      });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (ts?: Timestamp | null) => {
    if (!ts) return '—';
    try {
      return ts.toDate().toLocaleString();
    } catch {
      return '—';
    }
  };

  const isExpired = (invite: InviteRecord) => {
    if (!invite.expiresAt) return false;
    return invite.expiresAt.toDate() < new Date();
  };

  const pendingInvites = invites.filter(i => !i.usedAt && !isExpired(i));
  const usedOrExpiredInvites = invites.filter(i => i.usedAt || isExpired(i));

  return (
    <div className="space-y-6">
      {/* Send Invite Form */}
      <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Send New Invite</h3>
        <form onSubmit={handleSendInvite} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Email Address</label>
            <input
              type="email"
              required
              className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white focus:border-emerald-500 focus:outline-none"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="user@example.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Role</label>
              <select
                className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white focus:border-emerald-500 focus:outline-none"
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value as Role })}
              >
                {roleOptions.map(role => (
                  <option key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </option>
                ))}
              </select>
              {!isDev && (
                <p className="text-xs text-gray-500 mt-1">Admins can only invite users</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Expires in (days)</label>
              <input
                type="number"
                min={1}
                max={60}
                className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white focus:border-emerald-500 focus:outline-none"
                value={form.expiresInDays}
                onChange={e => setForm({ ...form, expiresInDays: Number(e.target.value) })}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="w-full px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold disabled:opacity-60 transition-colors"
          >
            {sending ? 'Sending...' : 'Send Invite'}
          </button>
        </form>

        {message && (
          <div
            className={`mt-4 p-3 rounded-lg text-sm ${
              message.type === 'success'
                ? 'bg-emerald-900/50 border border-emerald-700 text-emerald-200'
                : 'bg-red-900/50 border border-red-700 text-red-200'
            }`}
          >
            {message.text}
          </div>
        )}
      </div>

      {/* Pending Invites */}
      <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Pending Invites</h3>
          <span className="text-sm text-gray-400">{pendingInvites.length} pending</span>
        </div>

        {pendingInvites.length === 0 ? (
          <p className="text-sm text-gray-400">No pending invites</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {pendingInvites.map(invite => (
              <div
                key={invite.id}
                className="flex items-center justify-between p-3 bg-gray-900/60 rounded-lg border border-gray-700"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate">{invite.email}</div>
                  <div className="text-xs text-gray-400">
                    Role: {invite.role} • Expires: {formatDate(invite.expiresAt)}
                  </div>
                </div>
                <div className="flex gap-2 ml-2">
                  <button
                    onClick={() => resendInvite(invite)}
                    disabled={sending}
                    className="px-3 py-1.5 text-xs rounded bg-blue-700 hover:bg-blue-600 text-white disabled:opacity-60"
                  >
                    Resend
                  </button>
                  <button
                    onClick={() => revokeInvite(invite.id)}
                    className="px-3 py-1.5 text-xs rounded bg-red-700 hover:bg-red-600 text-white"
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Used/Expired Invites */}
      {usedOrExpiredInvites.length > 0 && (
        <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">History</h3>
            <span className="text-sm text-gray-400">{usedOrExpiredInvites.length} total</span>
          </div>

          <div className="space-y-2 max-h-48 overflow-y-auto">
            {usedOrExpiredInvites.slice(0, 20).map(invite => (
              <div
                key={invite.id}
                className="flex items-center justify-between p-3 bg-gray-900/40 rounded-lg border border-gray-800 opacity-60"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-300 truncate">{invite.email}</div>
                  <div className="text-xs text-gray-500">
                    {invite.usedAt ? (
                      <>Used: {formatDate(invite.usedAt)}</>
                    ) : (
                      <>Expired: {formatDate(invite.expiresAt)}</>
                    )}
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded ${
                    invite.usedAt
                      ? 'bg-emerald-900/50 text-emerald-300'
                      : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {invite.usedAt ? 'Used' : 'Expired'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

